import React from "react";
import { Link } from "react-router-dom";
import { ChevronRightIcon, CheckIcon } from "./icons";

// Empty state for a League Play section.
//
// Every League Play section is built empty-state-first: art, a bold line that
// says what isn't there, a grey line that says what happens next, and an
// optional footer action on the card's own hairline — the same footer shape as
// the College Club Challenges card, so the whole page shares one rhythm.
//
// House rule: no trailing punctuation in the title; the body keeps its period.
const LeagueEmptyState = ({ art, title, body, actionLabel, to, onAction, done }) => (
  <div className="rounded-2xl bg-white border border-gray-200">
    {/* Client Rev 4: "Remove the white box from the empty state Q image. Make it
        on a transparent background. This will allow us to slightly tighten the
        spacing between the image and No cash games open and the image and the top
        border."
        Both done. The PNGs were opaque near-white studio shots whose backdrop read
        as a grey plate on a white card; they are now cut out AND cropped to the
        trophy, so the box no longer contains ~40% dead space and the padding here
        is what actually sets the gap. */}
    <div className="px-5 pt-5 pb-5 flex flex-col items-center text-center">
      {art}
      <p className="mt-2 font-montserrat font-bold text-base md:text-lg text-[#0F4146]">{title}</p>
      {/* `body` may be a string, or an array when the copy has to break on a
          specific word rather than wherever the container happens to run out.
          One code path either way — a single-line body is just an array of one. */}
      <p className="mt-1 max-w-xs text-sm text-secondary">
        {(Array.isArray(body) ? body : [body]).map((line) => (
          <span key={line} className="block">
            {line}
          </span>
        ))}
      </p>
    </div>

    {actionLabel && (
      <div className="border-t border-divider">
        {/* `done` keeps the footer in place after a one-shot action (e.g. Notify
            Me) instead of dropping the row and shifting the card. */}
        {done ? (
          <p className="flex items-center justify-center gap-1.5 py-3.5 font-semibold text-sm md:text-base text-[#22A36B]">
            <CheckIcon className="w-4 h-4" />
            {actionLabel}
          </p>
        ) : to ? (
          <Link
            to={to}
            className="flex items-center justify-center gap-1 py-3.5 font-semibold text-sm md:text-base text-[#0F4146] hover:underline"
          >
            {actionLabel}
            <ChevronRightIcon className="w-4 h-4" />
          </Link>
        ) : (
          <button
            type="button"
            onClick={onAction}
            className="w-full flex items-center justify-center gap-1 py-3.5 font-semibold text-sm md:text-base text-[#0F4146] hover:underline"
          >
            {actionLabel}
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    )}
  </div>
);

// Art for the empty states — the Qushi trophy renders, straight from /public.
//
// The files are now TRANSPARENT cut-outs cropped to the trophy itself (client
// Rev 4). They used to be opaque near-white studio shots that each framed their
// trophy at a different scale inside a lot of empty backdrop, which is why the
// two variants needed different box sizes to look the same size on the page and
// why the card carried so much dead height. Cropped to their content they share
// one box, and the box is the trophy.
//
// No circle behind them and no `shadow-*` utility: each render carries its own
// ground shadow, and a box-shadow would draw a rectangle behind a transparent
// image.
const ART = {
  // `&` in the filename is percent-encoded so nothing downstream can mistake
  // the rest of the path for a query string.
  leagues: { src: "/features%26publicleaque.png", box: "h-20 w-20" },
  myLeagues: { src: "/myLeaqueempty.png", box: "h-20 w-20" },
  // Cash Games View All — the client's own illustration, supplied with the empty
  // state design (a market circle and a trophy coin).
  //
  // "The different tabs should use the same empty state image (size)." — ONE
  // entry, used by all three tabs, so the art cannot change size or subject
  // between them. The design draws it far larger than the section empty states
  // (a ~280px illustration rather than an 80px one), hence its own box: capped
  // at 280px and free to shrink on a narrow phone.
  //
  // "Remove the transparent background from the empty state image." — the file
  // they sent has a CHECKERBOARD painted into it (it is RGB, so the pattern is
  // pixels, not real transparency: their exporter drew the transparency
  // indicator into the render). It has been keyed out here, by flood-filling
  // the neutral near-white background in from the edges, which leaves the
  // artwork and its ground shadow untouched. Anything that replaces this file
  // needs the same treatment, or a clean re-export from them.
  cashGames: { src: "/cash-game-empty.png", box: "w-full max-w-[280px] h-auto" },
};

export const EmptyStateArt = ({ variant = "leagues" }) => {
  const art = ART[variant] || ART.leagues;
  return (
    <img
      src={art.src}
      alt=""
      loading="lazy"
      decoding="async"
      className={`${art.box} object-contain select-none pointer-events-none`}
    />
  );
};

export default LeagueEmptyState;
